import { useParams } from 'react-router';
import { BookOpen, CheckCircle2, Layers } from 'lucide-react';
import { ApiError } from '../lib/api.js';
import { useCoursePage } from '../lib/useLearnerData.js';
import { PageContainer, PageHeading } from '../components/layout/AppShell.js';
import { Card, EmptyState, ProgressBar, ProgressRing, SectionHeading, StatusBadge } from '../components/ui.js';
import { SessionTrack } from '../components/learner/SessionTrack.js';
import { ApiErrorView, TrackSkeleton } from '../components/learner/StateViews.js';
import { NotFoundPage } from './NotFoundPage.js';
import { pluralize } from '../lib/format.js';

/**
 * One course: where you are in it, and everything it contains in order.
 *
 * Locked sessions are part of the track, not missing from it — the whole point
 * of sequential unlock is that the learner can see what is ahead and why it is
 * not open yet.
 */
export function CoursePage() {
  const { courseSlug = '' } = useParams<{ courseSlug: string }>();
  const { data, isPending, error, refetch } = useCoursePage(courseSlug);

  if (error instanceof ApiError && error.isNotFound) {
    return (
      <NotFoundPage
        title="That course is not here"
        description="The address does not match any course you can open. It may have been renamed, archived, or never released to you."
      />
    );
  }

  if (isPending) {
    return (
      <PageContainer>
        <TrackSkeleton />
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <ApiErrorView error={error} retry={() => void refetch()} context="this course" />
      </PageContainer>
    );
  }

  const { course, sessions, capstone, progress } = data;
  const isDraft = course.status !== 'published';

  return (
    <PageContainer>
      <PageHeading
        backTo="/"
        backLabel="My learning"
        eyebrow="Course"
        title={course.title}
        description={course.summary || undefined}
        actions={isDraft ? <StatusBadge status={course.status} /> : undefined}
      />

      <Card className="p-5 mb-6">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
          <div className="flex items-center gap-3">
            <ProgressRing
              value={progress.pct}
              size={52}
              label={`${Math.round(progress.pct)}% of this course complete`}
            />
            <div>
              <p className="text-sm font-medium text-ink">Your progress</p>
              <p className="text-xs text-ink-muted">
                {progress.completedSessions} of {progress.totalSessions} sessions completed
              </p>
            </div>
          </div>

          <dl className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <div className="flex items-center gap-2">
              <Layers size={16} className="text-ink-subtle" aria-hidden />
              <div>
                <dt className="text-xs text-ink-subtle">Sessions</dt>
                <dd className="text-sm font-medium text-ink tabular-nums">
                  {course.sessionCount}
                </dd>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-ink-subtle" aria-hidden />
              <div>
                <dt className="text-xs text-ink-subtle">Completed</dt>
                <dd className="text-sm font-medium text-ink tabular-nums">
                  {progress.completedSessions}
                </dd>
              </div>
            </div>
            {course.sequentialUnlock ? (
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-ink-subtle" aria-hidden />
                <div>
                  <dt className="text-xs text-ink-subtle">Order</dt>
                  <dd className="text-sm font-medium text-ink">Unlocks in sequence</dd>
                </div>
              </div>
            ) : null}
          </dl>
        </div>

        <ProgressBar
          className="mt-4"
          value={progress.pct}
          label={`${course.title} overall progress`}
          showValue
        />
      </Card>

      <SectionHeading>
        The track
        <span className="sr-only"> — {pluralize(sessions.length, 'session')}</span>
      </SectionHeading>

      {sessions.length === 0 && !capstone ? (
        <EmptyState
          title="Nothing has been published in this course yet"
          description="The author has not released any sessions. When they do, they will appear here in order — you do not need to do anything to get them."
        />
      ) : (
        <SessionTrack courseSlug={course.slug} sessions={sessions} capstone={capstone} />
      )}
    </PageContainer>
  );
}
